// Contact management functions
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const CONTACTS_COLLECTION = 'contacts'

/**
 * Create a new contact
 * @param {Object} contactData - Contact data
 * @param {string} userId - Current user ID
 * @returns {Promise<string>} Document ID
 */
export const createContact = async (contactData, userId) => {
  try {
    const docRef = await addDoc(collection(db, CONTACTS_COLLECTION), {
      ...contactData,
      owner: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating contact:', error)
    throw error
  }
}

/**
 * Update an existing contact
 * @param {string} contactId - Contact document ID
 * @param {Object} updates - Fields to update
 */
export const updateContact = async (contactId, updates) => {
  try {
    const docRef = doc(db, CONTACTS_COLLECTION, contactId)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating contact:', error)
    throw error
  }
}

/**
 * Delete a contact
 * @param {string} contactId - Contact document ID
 * @param {string} userId - User ID (optional, for deleting associated activities)
 */
export const deleteContact = async (contactId, userId = null) => {
  console.log('deleteContact called with:', { contactId, userId })
  
  try {
    // Delete all associated activities first (dynamic import avoids init-order / TDZ issues)
    try {
      console.log('Attempting to delete activities for contact:', contactId)
      const { deleteContactActivities } = await import('./activities')
      if (userId) {
        await deleteContactActivities(contactId, userId)
      } else {
        await deleteContactActivities(contactId)
      }
      console.log('Activities deletion completed')
    } catch (activityError) {
      console.warn('Failed to delete activities, continuing with contact deletion:', activityError)
      // Continue with contact deletion even if activity deletion fails
    }
    
    // Then delete the contact
    console.log('Attempting to delete contact document:', contactId)
    const contactRef = doc(db, CONTACTS_COLLECTION, contactId)
    console.log('Contact ref:', contactRef)
    await deleteDoc(contactRef)
    console.log(`Successfully deleted contact ${contactId}`)
  } catch (error) {
    console.error('Error deleting contact:', error)
    console.error('Error code:', error.code)
    console.error('Error message:', error.message)
    throw error
  }
}

/**
 * Get a single contact by ID
 * @param {string} contactId - Contact document ID
 * @returns {Promise<Object|null>} Contact data
 */
export const getContact = async (contactId) => {
  try {
    const docRef = doc(db, CONTACTS_COLLECTION, contactId)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }
    return null
  } catch (error) {
    console.error('Error getting contact:', error)
    throw error
  }
}

/**
 * Get all contacts for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of contacts
 */
export const getContacts = async (userId) => {
  try {
    const q = query(
      collection(db, CONTACTS_COLLECTION),
      where('owner', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting contacts:', error)
    throw error
  }
}

/**
 * Subscribe to contacts changes (real-time)
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToContacts = (userId, callback) => {
  const q = query(
    collection(db, CONTACTS_COLLECTION),
    where('owner', '==', userId),
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const contacts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(contacts)
  }, (error) => {
    console.error('Error subscribing to contacts:', error)
  })
}

/**
 * Find a contact by email address
 * @param {string} email - Email address to search for
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Contact data or null if not found
 */
export const findContactByEmail = async (email, userId) => {
  try {
    if (!email) return null
    
    const q = query(
      collection(db, CONTACTS_COLLECTION),
      where('owner', '==', userId),
      where('email', '==', email.toLowerCase().trim())
    )
    
    const querySnapshot = await getDocs(q)
    if (querySnapshot.empty) {
      return null
    }
    
    const doc = querySnapshot.docs[0]
    return { id: doc.id, ...doc.data() }
  } catch (error) {
    console.error('Error finding contact by email:', error)
    throw error
  }
}

/**
 * Find a contact by name (case-insensitive)
 * @param {string} name - Contact name to search for
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Contact data or null if not found
 */
export const findContactByName = async (name, userId) => {
  try {
    if (!name || !name.trim()) return null
    
    // Get all contacts for the user and filter by name (case-insensitive)
    // Note: Firestore doesn't support case-insensitive queries directly,
    // so we fetch all contacts and filter in memory
    const q = query(
      collection(db, CONTACTS_COLLECTION),
      where('owner', '==', userId)
    )
    
    const querySnapshot = await getDocs(q)
    const normalizedSearchName = name.trim().toLowerCase()
    
    for (const doc of querySnapshot.docs) {
      const contactData = doc.data()
      const contactName = contactData.name?.trim().toLowerCase()
      
      if (contactName === normalizedSearchName) {
        return { id: doc.id, ...contactData }
      }
    }
    
    return null
  } catch (error) {
    console.error('Error finding contact by name:', error)
    throw error
  }
}

